import { useState, useCallback, useEffect, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAddPlanLine } from '@/hooks/usePlanDetail'
import { usePlanMetadata } from '@/hooks/usePlanMetadata'
import { useCurrentVersionId } from '@/hooks/useCurrentVersionId'
import { useExportPlanLines } from '@/hooks/useExportPlanLines'
import { PlanDetailHeader } from '@/components/PlanDetail/PlanDetailHeader'
import {
  PlanDetailTabs,
  TabValue,
} from '@/components/PlanDetail/PlanDetailTabs'
import { ProductsTable } from '@/components/PlanDetail/ProductsTable'
import { usePersistedPageSize } from '@/hooks/usePersistedPageSize'
import { TABLE_PAGE_SIZE_KEYS } from '@/stores/useTablePageSizeStore'
import { ImportItemsModal } from '@/components/PlanDetail/ImportItemsModal'
import { ReplaceDummySkusModal } from '@/components/PlanDetail/ReplaceDummySkusModal'
import { toast } from '@/stores/useToastStore'
import { buildHierarchyBreadcrumb } from '@/utils/formatters'
import { CommentsTab } from '@/components/PlanDetail/CommentsTab'
import { cn } from '@/utils/cn'
import { PlanDetailReportTab } from '@/components/PlanDetail/PlanDetailReportTab'
import {
  PLAN_DETAIL_TABS,
  resolveFinancialsReportType,
} from '@/config/powerbiReports'

const validTabs: TabValue[] = [
  'products',
  'summary',
  'financials',
  'analytics',
  'comments',
]

export default function AssortmentPlanDetail() {
  const { planId } = useParams<{ planId: string }>()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<TabValue>(() => {
    const tab = searchParams.get('tab') as TabValue | null
    return tab && validTabs.includes(tab) ? tab : 'products'
  })

  useEffect(() => {
    const tab = searchParams.get('tab') as TabValue | null
    if (tab && validTabs.includes(tab)) {
      setActiveTab(tab)
    } else if (!tab) {
      setActiveTab('products')
    }
  }, [searchParams])

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = usePersistedPageSize(
    TABLE_PAGE_SIZE_KEYS.PLAN_LINE_ITEMS,
    20
  )
  const [importModalOpen, setImportModalOpen] = useState(false)
  const [replaceDummySkusModalOpen, setReplaceDummySkusModalOpen] =
    useState(false)
  const [dummySkuLineItemIds, setDummySkuLineItemIds] = useState<string[]>([])
  const queryClient = useQueryClient()

  const { versionId, isViewingNonActiveVersion } = useCurrentVersionId(planId)

  // Fetch only plan metadata (no lines)
  const { data, isLoading } = usePlanMetadata(planId!)

  const { exportPlanLines, isProcessing: isExporting } = useExportPlanLines(
    planId!,
    versionId ?? null
  )
  const isReadOnly =
    !isViewingNonActiveVersion &&
    (data?.status === 'SUBMITTED' || data?.status === 'APPROVED')

  const addLineMutation = useAddPlanLine(planId!, data?.hierarchy)

  const handleAddItem = () => {
    addLineMutation.mutate(undefined, {
      onSuccess: () => {
        setPage(1)
        queryClient.invalidateQueries({
          queryKey: ['plan-detail', planId],
        })
        // Also invalidate DAG table rows so new line shows up
        queryClient.invalidateQueries({
          queryKey: ['dag-table', 'rows', planId],
        })
      },
      onError: (error: Error) => {
        toast.error(`Failed to add line: ${error.message}`)
      },
    })
  }

  // Reset page when pageSize changes
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(1)
  }

  // Reset to page 1 when active version changes
  useEffect(() => {
    setPage(1)
  }, [versionId])

  // Handle import completion - reset to page 1 to see imported items
  const handleImportComplete = () => {
    setPage(1)
  }

  // Handle bulk delete success - reset to page 1 to avoid empty page
  const handleBulkDeleteSuccess = () => {
    setPage(1)
  }

  // Handle replace dummy SKUs
  const handleReplaceDummySkus = (lineItemIds: string[]) => {
    setDummySkuLineItemIds(lineItemIds)
    setReplaceDummySkusModalOpen(true)
  }

  const handleReplaceDummySkusComplete = () => {
    setPage(1)
    queryClient.invalidateQueries({
      queryKey: ['dag-table', 'rows', planId],
    })
  }

  // Handle export
  const handleExport = useCallback(() => {
    exportPlanLines()
  }, [exportPlanLines])

  const financialsSavedFilters = useMemo(
    () => ({
      planId: planId ?? '',
      resolveReportType: resolveFinancialsReportType,
    }),
    [planId]
  )

  if (!planId) {
    return (
      <div className="flex items-center justify-center p-12 text-muted-foreground">
        Invalid plan ID
      </div>
    )
  }

  // Only show "Plan not found" when we're not loading and have no data
  if (!isLoading && !data) {
    return (
      <div className="flex items-center justify-center p-12 text-muted-foreground">
        Plan not found
      </div>
    )
  }

  const isReportTab =
    activeTab === 'summary' ||
    activeTab === 'financials' ||
    activeTab === 'analytics'

  return (
    <div
      className={cn('flex flex-col gap-6 p-6', isReportTab && 'h-full min-h-0')}
    >
      {/* Header */}
      <PlanDetailHeader
        planId={planId}
        name={data?.name ?? ''}
        status={data?.status ?? 'DRAFT'}
        planMetadata={data}
        hierarchyBreadcrumb={
          data?.hierarchy ? buildHierarchyBreadcrumb(data.hierarchy) : ''
        }
      />

      {/* Tabs */}
      <PlanDetailTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab content */}
      {activeTab === 'products' ? (
        <ProductsTable
          planId={planId}
          versionId={versionId}
          primaryAttributes={
            data?.primary_attributes ?? data?.primary_attribute_ids ?? []
          }
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          onAddItem={handleAddItem}
          onImport={() => setImportModalOpen(true)}
          onExport={handleExport}
          isExporting={isExporting}
          onBulkDeleteSuccess={handleBulkDeleteSuccess}
          isReadOnly={isReadOnly}
          planStatus={data?.status}
          onReplaceDummySkus={handleReplaceDummySkus}
        />
      ) : activeTab === 'summary' ? (
        <PlanDetailReportTab
          reportId={PLAN_DETAIL_TABS.summary.reportId}
          pageId={PLAN_DETAIL_TABS.summary.pageId}
          label="Summary"
          filterTarget={PLAN_DETAIL_TABS.summary.filterTarget}
          filterValue={planId}
          versionFilterTarget={PLAN_DETAIL_TABS.summary.versionFilterTarget}
          versionFilterValue={versionId ?? undefined}
        />
      ) : activeTab === 'financials' ? (
        <PlanDetailReportTab
          reportId={PLAN_DETAIL_TABS.financials.reportId}
          pageId={PLAN_DETAIL_TABS.financials.pageId}
          label="Financials"
          filterTarget={PLAN_DETAIL_TABS.financials.filterTarget}
          filterValue={planId}
          versionFilterTarget={PLAN_DETAIL_TABS.financials.versionFilterTarget}
          versionFilterValue={versionId ?? undefined}
          showPageNavigation={true}
          savedFilters={financialsSavedFilters}
        />
      ) : activeTab === 'analytics' ? (
        <PlanDetailReportTab
          reportId={PLAN_DETAIL_TABS.analytics.reportId}
          pageId={PLAN_DETAIL_TABS.analytics.pageId}
          label="Analytics"
          filterTarget={PLAN_DETAIL_TABS.analytics.filterTarget}
          filterValue={data?.name ?? ''}
          slicerTitle={PLAN_DETAIL_TABS.analytics.slicerTitle}
          versionFilterTarget={PLAN_DETAIL_TABS.analytics.versionFilterTarget}
          versionFilterValue={versionId ?? undefined}
        />
      ) : activeTab === 'comments' ? (
        <CommentsTab planId={planId} />
      ) : null}

      {/* Import Items Modal */}
      <ImportItemsModal
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        planId={planId}
        versionId={versionId!}
        onImportComplete={handleImportComplete}
      />

      {/* Replace Dummy SKUs Modal */}
      <ReplaceDummySkusModal
        open={replaceDummySkusModalOpen}
        onOpenChange={setReplaceDummySkusModalOpen}
        planId={planId}
        lineItemIds={dummySkuLineItemIds}
        onReplaceComplete={handleReplaceDummySkusComplete}
      />
    </div>
  )
}
